// src/dnd/views/Sidebar_parts/AssetUploadModal.js
import React, { useState, useEffect } from "react";

export default function AssetUploadModal({ isOpen, onClose, onCreated, mapId, user }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("image");
  const [blocksVision, setBlocksVision] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [zIndex, setZIndex] = useState(0);
  const [hidable, setHidable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setName("");
      setType("image");
      setBlocksVision(false);
      setOpacity(1);
      setZIndex(0);
      setHidable(false);
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  // If the type changes away from "image", clear image-only flags
  useEffect(() => {
    if (type !== "image") {
      setBlocksVision(false);
      setHidable(false);
    }
  }, [type]);

  const inferTypeFromFile = (f) => {
    // Only actively support Image + Roof overlay for now.
    if (!f || !f.type) return "image";
    if (f.type.startsWith("image/")) return "image";
    return "image";
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f && !name.trim()) {
      const n = f.name.replace(/\.[^.]+$/, "");
      setName(n);
    }
    if (f) {
      setType(inferTypeFromFile(f));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!mapId) {
      setError("Missing map id.");
      return;
    }
    if (!file) {
      setError("Please choose a file.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("map_id", String(mapId));
      fd.append("file", file);
      if (name) fd.append("name", name);
      if (type) {
        fd.append("asset_type", type);
        fd.append("type", type);
      }
      fd.append("opacity", String(opacity));
      fd.append("z_index", String(zIndex));

      // Only images can block vision / be hidable
      if (type === "image") {
        fd.append("blocks_vision", blocksVision ? "1" : "0");

        const meta = {};
        if (hidable) meta.hidable = true;
        if (Object.keys(meta).length > 0) {
          fd.append("description", JSON.stringify(meta));
        }
      }

      const res = await fetch(`/api/tabletop/assets/upload`, {
        method: "POST",
        body: fd,
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Upload failed");
      }
      if (typeof onCreated === "function") onCreated(data);
      if (typeof onClose === "function") onClose();
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#0008",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
    >
      <div
        style={{
          width: "min(560px, 92vw)",
          background: "#1f2027",
          color: "#fff",
          border: "1px solid #32333d",
          borderRadius: 10,
          boxShadow: "0 10px 28px #0008"
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #2b2c36",
            fontWeight: 800,
            fontSize: 18
          }}
        >
          Upload/Create Asset
        </div>
        <form
          onSubmit={onSubmit}
          style={{ padding: 16, display: "grid", gap: 10 }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <label style={{ fontSize: 13, opacity: 0.9 }}>File</label>
            <input
              type="file"
              onChange={onFileChange}
              disabled={submitting}
              style={{ color: "#ddd" }}
            />
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <label style={{ fontSize: 13, opacity: 0.9 }}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              placeholder="Asset name"
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #3a3b44",
                background: "#282931",
                color: "#fff"
              }}
            />
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <label style={{ fontSize: 13, opacity: 0.9 }}>Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={submitting}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #3a3b44",
                background: "#282931",
                color: "#fff"
              }}
            >
              {/* Active options */}
              <option value="image">Image</option>
              <option value="roof_overlay">Roof overlay</option>

              {/* Future options, greyed out */}
              <option value="obstacle" disabled>
                Obstacle/Wall Sprite (Coming soon)
              </option>
              <option value="icon_sprite" disabled>
                Icon/Sprite (Coming soon)
              </option>
              <option value="sfx_audio" disabled>
                Sound Effect (Coming soon)
              </option>
              <option value="video" disabled>
                Video (Coming soon)
              </option>
              <option value="other" disabled>
                Other (Coming soon)
              </option>
            </select>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10
            }}
          >
            <div style={{ display: "grid", gap: 6 }}>
              <label style={{ fontSize: 13, opacity: 0.9 }}>Opacity</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={opacity}
                onChange={(e) => setOpacity(Number(e.target.value))}
                disabled={submitting}
                style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #3a3b44",
                  background: "#282931",
                  color: "#fff"
                }}
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={{ fontSize: 13, opacity: 0.9 }}>Z Index</label>
              <input
                type="number"
                step="1"
                value={zIndex}
                onChange={(e) => setZIndex(Number(e.target.value))}
                disabled={submitting}
                style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #3a3b44",
                  background: "#282931",
                  color: "#fff"
                }}
              />
            </div>
          </div>

          {/* Only show these for plain Image assets */}
          {type === "image" && (
            <>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  userSelect: "none"
                }}
              >
                <input
                  type="checkbox"
                  checked={blocksVision}
                  onChange={(e) => setBlocksVision(e.target.checked)}
                  disabled={submitting}
                />
                Blocks vision (line of sight)
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  userSelect: "none"
                }}
              >
                <input
                  type="checkbox"
                  checked={hidable}
                  onChange={(e) => setHidable(e.target.checked)}
                  disabled={submitting}
                />
                Hidable (can be hidden to reveal doors / cave entrances)
              </label>
            </>
          )}

          {error && (
            <div style={{ color: "#ff6b6b", fontWeight: 700 }}>{error}</div>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 8
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                background: "#3a3b44",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 14px",
                fontWeight: 800,
                cursor: "pointer"
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !file || !mapId}
              style={{
                background: "#ffc73c",
                color: "#222",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                fontWeight: 800,
                cursor: submitting ? "wait" : "pointer"
              }}
            >
              {submitting ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
